import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { ChainId } from '@nominex/nomiswap-sdk'
import fromPairs from 'lodash-es/fromPairs'
import { getFarmsConfig } from 'config/farms'
import type { AppState } from 'state'
import { resetUserState } from 'state/global/actions'
import { SerializedFarm, SerializedFarmsState } from 'state/types'
import fetchFarms from './fetchFarms'
import {
  fetchFarmUserAllowances,
  fetchFarmUserEarnings,
  fetchFarmUserStakedBalances,
  fetchFarmUserTokenBalances,
} from './fetchFarmUser'
import fetchFarmsBonuses from './fetchFarmsBonuses'
import fetchFarmSupplyRates from './fetchFarmSupplyRates'
import { mergeFarmsByPid, serializeActionToKey, serializeConditionArgsToKey } from './utils'

interface FarmUserDataResponse {
  pid: number
  allowance: string
  tokenBalance: string
  stakedBalance: string
  earnings: string
}

const DEFAULT_USER_DATA = {
  allowance: '0',
  tokenBalance: '0',
  stakedBalance: '0',
  earnings: '0',
}

const initialState: SerializedFarmsState = {
  chainId: null,
  data: [],
  userDataLoaded: false,
  execKeys: {},
}

// Async thunks
export const fetchInitialFarmsData = createAsyncThunk<
  { data: SerializedFarm[]; chainId: number },
  { chainId: number },
  {
    state: AppState
  }
>('farms/fetchInitialFarmsData', ({ chainId }) => {
  const farmsConfig = getFarmsConfig(chainId)

  return {
    data: farmsConfig.map((farm) => ({
      ...farm,
      userData: DEFAULT_USER_DATA,
    })),
    chainId,
  }
})

// Async thunks

// Fetch farms by pids or farm configs
export const fetchFarmsPublicDataAsync = createAsyncThunk<
  SerializedFarm[],
  { chainId: ChainId; pids?: number[]; farms?: SerializedFarm[] },
  { state: AppState }
>(
  'farms/fetchFarmsPublicDataAsync',
  async ({ chainId, pids = [], farms = [] }, { dispatch, getState }) => {
    const state = getState()

    if (state.farms.chainId !== chainId) {
      await dispatch(fetchInitialFarmsData({ chainId }))
    }

    try {
      const farmsConfig = getFarmsConfig(chainId)
      const farmsToFetch = farms?.length ? farms : farmsConfig.filter((farmConfig) => pids.includes(farmConfig.pid))

      const results = await Promise.all([
        fetchFarms(farmsToFetch, chainId),
        fetchFarmSupplyRates(farmsToFetch, chainId),
      ])

      return mergeFarmsByPid([...results])
    } catch (error: any) {
      console.error('farms/fetchFarmsPublicDataAsync', error, [farms, pids])
      throw error
    }
  },
  {
    condition: (arg, { getState }) => {
      const { typePrefix } = fetchFarmsPublicDataAsync
      const { farms } = getState()

      if (farms.execKeys[serializeConditionArgsToKey(typePrefix, arg)]) {
        console.debug(`${typePrefix} is fetching, skipping extra call`)
        return false
      }
      return true
    },
  },
)

// Fetch farms user data by pids or farm configs
export const fetchFarmUserDataAsync = createAsyncThunk<
  FarmUserDataResponse[],
  { account: string; chainId: ChainId; pids?: number[]; farms?: SerializedFarm[] },
  { state: AppState }
>(
  'farms/fetchFarmUserDataAsync',
  async ({ account, chainId, pids = [], farms = [] }, { dispatch, getState }) => {
    const state = getState()

    if (state.farms.chainId !== chainId) {
      await dispatch(fetchInitialFarmsData({ chainId }))
    }

    const farmsConfig = getFarmsConfig(chainId)
    const farmsToFetch = farms?.length ? farms : farmsConfig.filter((farmConfig) => pids.includes(farmConfig.pid))

    const [userFarmAllowances, userFarmTokenBalances, userStakedBalances, userStakedEarnings] = await Promise.all([
      fetchFarmUserAllowances(account, chainId, farmsToFetch),
      fetchFarmUserTokenBalances(account, chainId, farmsToFetch),
      fetchFarmUserStakedBalances(account, chainId, farmsToFetch),
      fetchFarmUserEarnings(account, chainId, farmsToFetch),
    ])

    return userFarmAllowances.map((farmAllowance, index) => ({
      pid: farmsToFetch[index].pid,
      allowance: userFarmAllowances[index],
      tokenBalance: userFarmTokenBalances[index],
      stakedBalance: userStakedBalances[index],
      earnings: userStakedEarnings[index],
    }))
  },
  {
    condition: (arg, { getState }) => {
      const { typePrefix } = fetchFarmUserDataAsync
      const { farms } = getState()

      if (farms.execKeys[serializeConditionArgsToKey(typePrefix, arg)]) {
        console.debug(`${typePrefix} is fetching, skipping extra call`)
        return false
      }

      return true
    },
  },
)

// Fetch farm bonuses by pids or farm configs
export const fetchFarmBonusesAsync = createAsyncThunk<
  SerializedFarm[],
  { chainId: ChainId; pids?: number[]; farms?: SerializedFarm[] },
  { state: AppState }
>(
  'farms/fetchFarmBonusesAsync',
  async ({ chainId, pids = [], farms = [] }, { dispatch, getState }) => {
    try {
      const state = getState()

      if (state.farms.chainId !== chainId) {
        await dispatch(fetchInitialFarmsData({ chainId }))
      }

      const farmsConfig = getFarmsConfig(chainId)
      const farmsToFetch = farms?.length ? farms : farmsConfig.filter((farmConfig) => pids.includes(farmConfig.pid))

      const fetchedFarms = await fetchFarmsBonuses(farmsToFetch)

      return mergeFarmsByPid([fetchedFarms])
    } catch (error: any) {
      console.error('farms/fetchFarmBonusesAsync', error, [farms, pids])
      throw error
    }
  },
  {
    condition: (arg, { getState }) => {
      const { typePrefix } = fetchFarmBonusesAsync
      const { farms } = getState()

      if (farms.execKeys[serializeConditionArgsToKey(typePrefix, arg)]) {
        console.debug(`${typePrefix} is fetching, skipping extra call`)
        return false
      }
      return true
    },
  },
)

export const farmsSlice = createSlice({
  name: 'Farms',
  initialState,
  reducers: {
    appendFarm(state, action) {
      const isFarmExists = state.data.findIndex((farm) => farm.pid === action.payload.pid) >= 0
      if (!isFarmExists) {
        state.data = mergeFarmsByPid([state.data, [{ ...action.payload, userData: DEFAULT_USER_DATA }]])
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInitialFarmsData.fulfilled, (state, action) => {
      const { data, chainId } = action.payload
      state.data = data
      state.chainId = chainId
    })

    // Update farms with live data
    builder.addCase(fetchFarmsPublicDataAsync.fulfilled, (state, action) => {
      state.data = mergeFarmsByPid([state.data, action.payload])
    })

    // Update farms with user data
    builder.addCase(fetchFarmUserDataAsync.fulfilled, (state, action) => {
      const userDataMap = fromPairs(action.payload.map((userDataEl) => [userDataEl.pid, userDataEl]))
      state.data = state.data.map((farm) => {
        const userDataEl = userDataMap[farm.pid]

        return userDataEl ? { ...farm, userData: userDataEl } : farm
      })
      state.userDataLoaded = true
    })

    // Update farms with bonuses data
    builder.addCase(fetchFarmBonusesAsync.fulfilled, (state, action) => {
      state.data = mergeFarmsByPid([state.data, action.payload])
    })

    // Reset farms state after logout
    builder.addCase(resetUserState, (state) => {
      state.data = state.data.map((farm) => ({
        ...farm,
        userData: {
          allowance: '0',
          tokenBalance: '0',
          stakedBalance: '0',
          earnings: '0',
        },
      }))
      state.userDataLoaded = false
    })

    // Condition matchers to avoid extra calls
    builder.addMatcher(
      isAnyOf(fetchFarmUserDataAsync.pending, fetchFarmsPublicDataAsync.pending, fetchFarmBonusesAsync.pending),
      (state, action) => {
        state.execKeys[serializeActionToKey(action)] = true
      },
    )

    builder.addMatcher(
      isAnyOf(fetchFarmUserDataAsync.fulfilled, fetchFarmsPublicDataAsync.fulfilled, fetchFarmBonusesAsync.fulfilled),
      (state, action) => {
        state.execKeys[serializeActionToKey(action)] = false
      },
    )

    builder.addMatcher(
      isAnyOf(fetchFarmsPublicDataAsync.rejected, fetchFarmUserDataAsync.rejected, fetchFarmBonusesAsync.rejected),
      (state, action) => {
        state.execKeys[serializeActionToKey(action)] = false
      },
    )
  },
})

export const { appendFarm } = farmsSlice.actions

export default farmsSlice.reducer
